import React, { useId, useRef, useState } from "react";
import { InfoIcon } from "../../assets/icons/infoIcon";
import {
  StyledCounter,
  StyledEditIconWrapper,
  StyledErrorMessageWrapper,
  StyledInfoWrapper,
  StyledLabel,
  StyledLabelInfoGroup,
  StyledTextarea,
  StyledTextareaWrapper,
  StyledTopRow,
} from "./FormFieldStyles";
import { Tooltip } from "./Tooltip";
import { EditPenIcon } from "../../assets/icons/editPenIcon";
import { ErrorIcon } from "../../assets/icons/errorIcon";
import { Text } from "../../utils/text";
import { RequiredIndicator } from "./Form";
import { designSystemVars as vars } from "../tokens";

type TextareaProps = {
  mandatory?: boolean;
  variant?: "default" | "prompt";
  processing?: boolean;
  isRequired?: boolean;
  label?: string;
  value?: string | null;
  maxLength?: number;
  showCounter?: boolean;
  onChange?: (e: React.ChangeEvent<HTMLTextAreaElement>) => void;
  error?: string;
  tooltip?: React.ReactNode;
  attachments?: React.ReactNode;
  rows?: number;
  showResize?: boolean;
  showEditIcon?: boolean;
  disabled?: boolean;
  $disabled?: boolean;
  tooltipTabIndex?: 0 | -1;
  tooltipTriggerTabIndex?: 0 | -1;
  id?: string;
  name?: string;
  placeholder?: string;
  "aria-label"?: string;
};

export const TextareaField: React.FC<TextareaProps> = ({
  label,
  variant = "default",
  processing = false,
  isRequired = false,
  value = "",
  maxLength = 250,
  showCounter = false,
  onChange,
  error,
  tooltip = "",
  attachments,
  rows,
  showResize = true,
  showEditIcon,
  disabled = false,
  $disabled = false,
  tooltipTabIndex = 0,
  tooltipTriggerTabIndex,
  id,
  name,
  placeholder,
  "aria-label": ariaLabel,
}) => {
  const [isKeyboardFocus, setIsKeyboardFocus] = useState(false);
  const stringValue = typeof value === "string" ? value : String(value ?? "");
  const isExceeded = stringValue.length > maxLength;
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const mouseDownRef = useRef(false);
  const generatedId = useId();
  const controlId = id || generatedId;
  const errorId = `${controlId}-error`;
  const isProcessing = variant === "prompt" && processing;
  const visuallyDisabled = (disabled || $disabled) && !isProcessing;

  const handleFocus = () => {
    setIsKeyboardFocus(!mouseDownRef.current);
    mouseDownRef.current = false;

    if (!inputRef.current) return;

    const length = inputRef.current.value.length;
    inputRef.current.setSelectionRange(length, length);
  };

  const labelRow = (label || showCounter) && (
    <StyledTopRow>
      <StyledLabelInfoGroup>
        {label && (
          <StyledLabel
            htmlFor={controlId}
            haveError={variant === "prompt" && !!error}
          >
            {label}
            {isRequired && <RequiredIndicator> *</RequiredIndicator>}
          </StyledLabel>
        )}
        {tooltip && (
          <Tooltip
            tooltip={tooltip}
            triggerTabIndex={tooltipTriggerTabIndex}
            triggerAriaLabel={
              tooltipTriggerTabIndex !== undefined
                ? `${label} information`
                : undefined
            }
            fullWidth={tooltipTriggerTabIndex === undefined}
          >
            <StyledInfoWrapper
              tabIndex={
                tooltipTriggerTabIndex === undefined
                  ? tooltipTabIndex
                  : undefined
              }
              aria-hidden={
                tooltipTriggerTabIndex !== undefined ? true : undefined
              }
            >
              <InfoIcon />
            </StyledInfoWrapper>
          </Tooltip>
        )}
      </StyledLabelInfoGroup>

      {showCounter && (
        <StyledCounter isExceeded={isExceeded}>
          {stringValue.length}/{maxLength}
        </StyledCounter>
      )}
    </StyledTopRow>
  );

  const validationMessage = error && (
    <StyledErrorMessageWrapper
      id={errorId}
      role="alert"
      style={
        variant === "prompt"
          ? { width: "100%", height: "auto", minHeight: "30px", marginTop: 0 }
          : undefined
      }
    >
      <ErrorIcon aria-hidden="true" />
      <Text
        style={variant === "prompt" ? { minWidth: 0, flex: 1 } : undefined}
        fontFamily={vars.typography.family.sans}
        fontSize="var(--inzeloop-ds-font-size-xs)"
        fontStyle="normal"
        fontWeight="var(--inzeloop-ds-font-weight-regular)"
        $lineHeight="var(--inzeloop-ds-line-height-sm)"
        color={vars.color.control.errorText}
      >
        {error}
      </Text>
    </StyledErrorMessageWrapper>
  );

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        marginBottom:
          variant === "prompt"
            ? vars.spacing.none
            : "var(--inzeloop-ds-spacing-30px)",
        width: "100%",
        gap: variant === "prompt" ? vars.spacing.md : undefined,
      }}
    >
      {variant === "prompt" && (labelRow || validationMessage) && (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: vars.spacing.xs,
          }}
        >
          {labelRow}
          {validationMessage}
        </div>
      )}
      <StyledTextareaWrapper
        $variant={variant}
        $processing={isProcessing}
        $hasAttachments={variant === "prompt" && !!attachments}
        $haveError={!!error}
        onClick={(event) => {
          if (
            tooltipTriggerTabIndex !== undefined &&
            (event.target as Element).closest("[data-tooltip-trigger]")
          )
            return;
          inputRef.current?.focus();
        }}
        onMouseDown={() => {
          mouseDownRef.current = true;
        }}
        $disabled={visuallyDisabled}
        $focusVisible={isKeyboardFocus}
        $resizable={showResize}
      >
        {variant !== "prompt" && labelRow}

        {showEditIcon && (
          <StyledEditIconWrapper title="Edit">
            <EditPenIcon />
          </StyledEditIconWrapper>
        )}

        <StyledTextarea
          ref={inputRef}
          id={controlId}
          name={name}
          placeholder={placeholder}
          onFocus={handleFocus}
          onBlur={() => setIsKeyboardFocus(false)}
          value={stringValue}
          onChange={onChange}
          disabled={disabled || isProcessing}
          rows={rows || 3}
          $disabled={visuallyDisabled}
          $processing={isProcessing}
          aria-label={ariaLabel}
          aria-invalid={!!error}
          aria-required={isRequired}
          aria-describedby={error ? errorId : undefined}
          {...(showCounter ? { maxLength } : {})}
        />
        {variant === "prompt" && attachments && (
          <div onClick={(event) => event.stopPropagation()}>{attachments}</div>
        )}
      </StyledTextareaWrapper>
      {variant !== "prompt" && validationMessage}
    </div>
  );
};
